import { filter, includes, indexOf, slice, uniq } from 'es-toolkit/compat';
import React from 'react';

type RangeSelectionItem = string | number;

interface UseRangeSelectionOptions<Item extends RangeSelectionItem> {
  orderedItems: Item[];
  selectedItems: Item[];
  setSelectedItems: React.Dispatch<React.SetStateAction<Item[]>>;
}

export const useRangeSelection = <Item extends RangeSelectionItem>({
  orderedItems,
  selectedItems,
  setSelectedItems,
}: UseRangeSelectionOptions<Item>) => {
  const anchorItem = React.useRef<Item>();
  const [previewedItem, setPreviewedItem] = React.useState<Item>();
  const [rangePreviewActive, setRangePreviewActive] = React.useState(false);

  const getToggledSelection = (item: Item, selectRange: boolean) => {
    const itemIsSelected = includes(selectedItems, item);
    const anchorIndex = indexOf(orderedItems, anchorItem.current);
    const itemIndex = indexOf(orderedItems, item);

    if (!selectRange || anchorIndex === -1 || itemIndex === -1) {
      return itemIsSelected ? filter(selectedItems, (selectedItem) => selectedItem !== item) : [...selectedItems, item];
    }

    const rangeStart = Math.min(anchorIndex, itemIndex);
    const rangeEnd = Math.max(anchorIndex, itemIndex);
    const rangeItems = slice(orderedItems, rangeStart, rangeEnd + 1);
    const anchorIsSelected = includes(selectedItems, orderedItems[anchorIndex]);
    return anchorIsSelected
      ? uniq([...selectedItems, ...rangeItems])
      : filter(selectedItems, (selectedItem) => !includes(rangeItems, selectedItem));
  };

  const previewedSelectedItems =
    rangePreviewActive && previewedItem !== undefined && anchorItem.current !== undefined
      ? getToggledSelection(previewedItem, true)
      : undefined;

  React.useEffect(() => {
    if (previewedItem === undefined) return undefined;

    const handleShiftKey = (event: KeyboardEvent) => {
      if (event.key === 'Shift') setRangePreviewActive(event.type === 'keydown');
    };
    window.addEventListener('keydown', handleShiftKey);
    window.addEventListener('keyup', handleShiftKey);
    return () => {
      window.removeEventListener('keydown', handleShiftKey);
      window.removeEventListener('keyup', handleShiftKey);
    };
  }, [previewedItem]);

  const clearSelectionPreview = () => {
    setPreviewedItem(undefined);
    setRangePreviewActive(false);
  };

  const previewSelection = (item: Item, selectRange = false) => {
    setPreviewedItem(item);
    setRangePreviewActive(selectRange);
  };

  const resetRangeSelection = () => {
    anchorItem.current = undefined;
    clearSelectionPreview();
  };

  const toggleSelection = (item: Item, selectRange = false) => {
    setSelectedItems(getToggledSelection(item, selectRange));
    clearSelectionPreview();

    if (!selectRange || anchorItem.current === undefined) anchorItem.current = item;
  };

  return {
    clearSelectionPreview,
    previewedSelectedItems,
    previewSelection,
    resetRangeSelection,
    toggleSelection,
  };
};
